"use client";

import { ReactNode, useMemo } from "react";
import { X } from "lucide-react";
import clsx from "clsx";

import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { CustomModal } from "@/components/ui/CustomModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import { Asset } from "../types";
import { useMediaManager } from "../useMediaManager";
import { formatBytes, matchesPickerAsset } from "../utils";
import { AssetGrid } from "./AssetGrid";
import { AssetList } from "./AssetList";
import { Breadcrumbs } from "./Breadcrumbs";
import { ExplorerTree } from "./ExplorerTree";
import { FolderGrid } from "./FolderGrid";
import { FolderList } from "./FolderList";
import { MediaToolbar } from "./MediaToolbar";
import { AnimatePresence } from "framer-motion";
import { getAssetVariantParent, getAssetVariants } from "../assetVariants";
import { AssetPreview } from "./AssetPreview";

export type MediaUsageContext = {
  context: string;
  productId?: string;
  contentId?: string;
};

type MediaPickerOverlayProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: Asset, usage?: MediaUsageContext) => void;
  accept?: string[];
  title?: string;
  description?: ReactNode;
  usage?: MediaUsageContext;
  showMainImages?: boolean;
};

export function MediaPickerOverlay({
  open,
  onClose,
  onSelect,
  accept,
  title = "Select media",
  description,
  usage,
  showMainImages = false,
}: MediaPickerOverlayProps) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { state, actions } = useMediaManager({ accept });
  const visibleAssets = useMemo(
    () =>
      state.filteredAssets.filter((asset) =>
        matchesPickerAsset(asset, accept, showMainImages),
      ),
    [accept, showMainImages, state.filteredAssets],
  );
  const selectedAsset = state.selectedAsset
    ? (visibleAssets.find((asset) => asset.id === state.selectedAsset?.id) ??
      null)
    : null;
  const selectedVariants = selectedAsset
    ? getAssetVariants(selectedAsset, visibleAssets)
    : [];
  const selectedVariantParent = selectedAsset
    ? getAssetVariantParent(selectedAsset, visibleAssets)
    : null;

  const goToPath = (path: string | null) => {
    if (!path) return actions.goToFolder();
    const found = Object.values(state.tree).find(
      (node) => node.folder.path === path,
    );
    if (found) actions.goToFolder(found.folder.id);
  };

  const acceptLabel = useMemo(
    () => (accept && accept.length ? accept.join(", ") : "Any"),
    [accept],
  );

  const handleSelect = (asset: Asset) => {
    onSelect(asset, usage);
    onClose();
  };

  const body = (
    <div className="flex h-180 flex-col gap-3 px-4 pb-4">
      <div className="mt-1 flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-lg font-semibold text-gray-900">{title}</div>
          {description ? (
            <div className="text-sm text-gray-600">{description}</div>
          ) : null}
          <div className="text-xs text-gray-500">Filter: {acceptLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="bg-pinkCustom rounded px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
            onClick={() => {
              if (selectedAsset) handleSelect(selectedAsset);
            }}
            disabled={!selectedAsset}
          >
            Use selected
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-grayCustom text-slateCustom hover:text-pinkCustom flex h-9 w-9 items-center justify-center rounded-full transition"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={4} />
          </button>
        </div>
      </div>

      <div className="grid h-full grid-cols-4 overflow-hidden rounded-lg border border-gray-300 lg:grid-cols-5">
        <div className="hidden border-r border-gray-300 bg-white p-3 lg:block">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Explorer</h2>
            {state.loadingFolders ? (
              <span className="text-xs text-gray-500">loading...</span>
            ) : null}
          </div>
          <div className="h-full overflow-auto pr-1">
            <ExplorerTree
              tree={state.tree}
              rootId={state.rootId}
              currentFolderId={state.currentFolder?.id ?? null}
              selectedAssetId={selectedAsset?.id ?? null}
              loading={state.loadingFolders}
              onFolderClick={(id) => actions.goToFolder(id)}
              onToggleNode={(folder) => actions.toggleNode(folder)}
              onAssetClick={(asset) => {
                actions.selection.selectSingleAsset(asset.id);
              }}
              assetFilter={(asset) =>
                matchesPickerAsset(asset, accept, showMainImages)
              }
            />
          </div>
        </div>

        <div
          className={clsx(
            "bg-white",
            selectedAsset ? "col-span-4 lg:col-span-3" : "col-span-4",
          )}
        >
          <MediaToolbar
            canGoBack={
              !!state.currentFolder &&
              state.currentFolder.parentId !== null &&
              !state.loadingFolders
            }
            onBack={() =>
              actions.goToFolder(state.currentFolder?.parentId ?? null)
            }
            onRefresh={() => actions.goToFolder(state.currentFolder?.id)}
            busyAction={state.busyAction}
            selectedFolders={0}
            selectedAssets={selectedAsset ? 1 : 0}
            onDelete={() => {}}
            onMove={() => {}}
            onPaste={() => {}}
            hasPendingMove={false}
            onToggleViewMode={() =>
              actions.setViewMode(state.viewMode === "list" ? "grid" : "list")
            }
            viewMode={state.viewMode}
            onCreateFolder={() => {}}
            onUploadClick={() => {}}
            uploading={false}
            breadcrumbs={
              <Breadcrumbs
                breadcrumbs={state.breadcrumbs}
                onNavigatePath={(path) => goToPath(path)}
              />
            }
            showSelectionActions={false}
            showCreateActions={false}
            showUpload={false}
          />

          <div className="bg-grayCustom flex flex-wrap items-center gap-1 border-b border-gray-300 px-4 py-2 text-xs text-gray-600 lg:hidden">
            <Breadcrumbs
              breadcrumbs={state.breadcrumbs}
              onNavigatePath={(path) => goToPath(path)}
            />
          </div>

          <div className="bg-grayCustom grid grid-cols-3 items-center border-b border-gray-300 px-4 py-2 text-[11px] font-semibold text-gray-600 uppercase">
            <div className="col-span-2">Name</div>
            <div className="text-right">Size</div>
          </div>

          <div className="h-140 overflow-y-auto">
            {state.viewMode === "grid" ? (
              <div className="grid grid-cols-2 gap-2 p-2 sm:grid-cols-3 lg:grid-cols-4">
                <FolderGrid
                  folders={state.childFolders}
                  inlineFolderName={null}
                  onInlineNameChange={() => {}}
                  onInlineCreate={() => {}}
                  onInlineCancel={() => {}}
                  selectedFolderIds={[]}
                  onFolderClick={(id) => actions.goToFolder(id)}
                  onFolderContextMenu={() => {}}
                  onToggleFolderSelection={() => {}}
                  onRenameFolder={() => {}}
                  renameFolderId={null}
                  showCheckbox={false}
                  enableContextMenu={false}
                />
                <AssetGrid
                  assets={visibleAssets}
                  selectedAssetIds={selectedAsset ? [selectedAsset.id] : []}
                  onAssetClick={(asset) =>
                    actions.selection.selectSingleAsset(asset.id)
                  }
                  onAssetContextMenu={() => {}}
                  onToggleAssetSelection={() => {}}
                  onRenameAsset={() => {}}
                  renameAssetId={null}
                  showCheckbox={false}
                  enableContextMenu={false}
                />
              </div>
            ) : (
              <>
                <FolderList
                  folders={state.childFolders}
                  inlineFolderName={null}
                  onInlineNameChange={() => {}}
                  onInlineCreate={() => {}}
                  onInlineCancel={() => {}}
                  selectedFolderIds={[]}
                  onFolderClick={(id) => actions.goToFolder(id)}
                  onFolderContextMenu={() => {}}
                  onToggleFolderSelection={() => {}}
                  onRenameFolder={() => {}}
                  renameFolderId={null}
                  showCheckbox={false}
                  enableContextMenu={false}
                />
                <AssetList
                  assets={visibleAssets}
                  selectedAssetIds={selectedAsset ? [selectedAsset.id] : []}
                  onAssetClick={(asset) =>
                    actions.selection.selectSingleAsset(asset.id)
                  }
                  onAssetDoubleClick={(asset) => handleSelect(asset)}
                  onAssetContextMenu={() => {}}
                  onToggleAssetSelection={() => {}}
                  onRenameAsset={() => {}}
                  renameAssetId={null}
                  showCheckbox={false}
                  enableContextMenu={false}
                />
              </>
            )}

            {state.childFolders.length === 0 && visibleAssets.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-gray-500">
                No assets found
              </div>
            ) : null}
          </div>
        </div>

        {selectedAsset ? (
          <div className="hidden w-full border-l border-gray-300 bg-white p-4 lg:block xl:h-full xl:min-h-120">
            <div className="space-y-2 text-xs text-gray-700">
              <div className="text-sm font-semibold text-gray-900">
                {selectedAsset.originalFilename}
              </div>
              <div>Mime: {selectedAsset.mimeType}</div>
              <div>Status: {selectedAsset.status}</div>
              <div>Size: {formatBytes(selectedAsset.size)}</div>
              {selectedVariantParent ? (
                <button
                  type="button"
                  className="text-left text-blue-700 underline"
                  onClick={() =>
                    actions.selection.selectSingleAsset(
                      selectedVariantParent.id,
                    )
                  }
                >
                  Variant of: {selectedVariantParent.originalFilename}
                </button>
              ) : null}
              <div className="overflow-hidden rounded border border-gray-200 bg-white p-2">
                <AssetPreview asset={selectedAsset} detail />
              </div>
              <button
                className="bg-pinkCustom mt-3 rounded px-3 py-2 text-xs font-semibold text-white hover:opacity-90"
                onClick={() => handleSelect(selectedAsset)}
              >
                Use this asset
              </button>
              {selectedVariants.length ? (
                <div className="mt-3 border-t border-gray-200 pt-3">
                  <div className="mb-2 text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    Variants
                  </div>
                  <div className="space-y-2">
                    {selectedVariants.map((variant) => (
                      <button
                        type="button"
                        key={variant.id}
                        className="hover:bg-pinkCustom/5 w-full rounded border border-gray-200 bg-white p-2 text-left"
                        onClick={() =>
                          actions.selection.selectSingleAsset(variant.id)
                        }
                        onDoubleClick={() => handleSelect(variant)}
                      >
                        <div className="truncate text-xs font-medium text-gray-800">
                          {variant.originalFilename}
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500">
                          {formatBytes(variant.size)}
                          {variant.width && variant.height
                            ? ` · ${variant.width}x${variant.height}`
                            : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {open ? (
        isDesktop ? (
          <CustomModal
            open
            onOpenChange={(next) => {
              if (!next) onClose();
            }}
            maxWidth="max-w-7xl"
            maxHeight="90vh"
            bodyClassName="p-0"
          >
            {body}
          </CustomModal>
        ) : (
          <OverlayWrapper
            onClose={onClose}
            snapPoints={["85vh", "95vh"]}
            dismissOnOverlay
          >
            {body}
          </OverlayWrapper>
        )
      ) : null}
    </AnimatePresence>
  );
}
